import { useCloseUnreadTasksQuery } from "@api/closeUnreadTasks.api";
import { pluralize } from "@common/helpers/string";
import { Loader } from "@design-system/Loader/Loader";
import { PanelEmptyMessage } from "@design-system/PanelEmptyMessage/PanelEmptyMessage";
import { buildTaskOpenUrl } from "@hooks/useOpenWorkRow";
import { ChatIcon } from "@icons";
import styles from "./WorkDetailPane.module.scss";

interface WorkNotificationsSectionProps {
    closeId: string;
    workspaceId: string;
}

/**
 * Tasks in the work item with chat messages I haven't read, newest first.
 * Each opens its task with the chat in the client's workspace, in a new tab.
 */
export const WorkNotificationsSection = ({
    closeId,
    workspaceId,
}: Readonly<WorkNotificationsSectionProps>) => {
    const {
        data: tasks,
        isPending,
        isError,
    } = useCloseUnreadTasksQuery(closeId);

    if (isPending) {
        return <Loader inline />;
    }
    if (isError) {
        return (
            <PanelEmptyMessage>Failed to load notifications</PanelEmptyMessage>
        );
    }
    if (tasks.length === 0) {
        return <PanelEmptyMessage>No unread messages</PanelEmptyMessage>;
    }

    return (
        <ul className={styles.list}>
            {tasks.map(({ taskId, title, unreadCount }) => (
                <li key={taskId}>
                    <a
                        className={styles.listLink}
                        href={buildTaskOpenUrl({
                            workspaceId,
                            taskId,
                            panel: "chat",
                        })}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <span className={styles.listLinkTitle}>{title}</span>
                        <span className={styles.unreadCount}>
                            <ChatIcon aria-hidden />
                            {unreadCount}
                            <span className={styles.visuallyHidden}>
                                {` unread ${pluralize("message", unreadCount)}`}
                            </span>
                        </span>
                    </a>
                </li>
            ))}
        </ul>
    );
};
